// Allocate CA, losses and donations in priority order. The adapter determines
// the claimant's budget after its own losses and donations.
// The caller supplies the shared app computation adapter; this module contains
// no exemption, tax, currency conversion or current-year availability formulas.
const CURRENT_YEAR_CATEGORIES = [
  {
    key: "ca",
    transferKey: "caTransfer",
    availableKey: "currentYearCAAvailable",
  },
  {
    key: "losses",
    transferKey: "lossesTransfer",
    availableKey: "currentYearLossesAvailable",
  },
  {
    key: "donations",
    transferKey: "donationsTransfer",
    availableKey: "currentYearDonationsAvailable",
  },
];
export function createGroupReliefOptimiser({
  calculateCompanyComputation,
  summariseComputations,
}) {
  if (
    typeof calculateCompanyComputation !== "function" ||
    typeof summariseComputations !== "function"
  )
    throw new Error("A shared computation adapter is required.");

  function getQualifyingDays(company, basisDays) {
    return company.shareholdingMaintainedThroughout
      ? basisDays
      : company.continuousQualifyingDays;
  }

  function buildAdjustments(companies) {
    return Object.fromEntries(
      companies.map((company) => [
        company.id,
        { caTransfer: 0, lossesTransfer: 0, donationsTransfer: 0 },
      ]),
    );
  }

  function relationshipKey(transferorId, claimantId) {
    return `${transferorId}::${claimantId}`;
  }

  function addToTransfer(
    transfers,
    transferor,
    claimant,
    pairQualifyingDays,
    categoryKey,
    amount,
  ) {
    const key = relationshipKey(transferor.id, claimant.id);
    const existing = transfers.get(key) || {
      transferorId: transferor.id,
      claimantId: claimant.id,
      caTransferred: 0,
      lossesTransferred: 0,
      donationsTransferred: 0,
      totalTransferred: 0,
      pairQualifyingDays,
    };
    if (categoryKey === "ca") existing.caTransferred += amount;
    if (categoryKey === "losses") existing.lossesTransferred += amount;
    if (categoryKey === "donations") existing.donationsTransferred += amount;
    existing.totalTransferred += amount;
    transfers.set(key, existing);
  }

  function calculateClaimCapacity(company, caseSettings, adjustments) {
    const computation = calculateCompanyComputation(
      company,
      caseSettings,
      adjustments[company.id],
    );
    return Math.max(0, computation.claimCapacity);
  }

  function scoreClaimant(
    company,
    caseSettings,
    adjustments,
    transferKey,
    amount,
  ) {
    const before = calculateCompanyComputation(
      company,
      caseSettings,
      adjustments[company.id],
    );
    const simulatedAdjustments = {
      ...adjustments[company.id],
      [transferKey]: adjustments[company.id][transferKey] - amount,
    };
    const after = calculateCompanyComputation(
      company,
      caseSettings,
      simulatedAdjustments,
    );
    return before.taxPayable - after.taxPayable;
  }

  function optimiseGroupRelief(companies, caseSettings, basisDays) {
    const orderedCompanies = [...companies].sort(
      (a, b) => a.stableOrder - b.stableOrder,
    );
    const preComputations = orderedCompanies.map((company) =>
      calculateCompanyComputation(company, caseSettings),
    );
    const beforeSummary = summariseComputations(preComputations);
    const adjustments = buildAdjustments(orderedCompanies);
    const transfers = new Map();

    const transferors = orderedCompanies
      .map((company, index) => ({ company, pre: preComputations[index] }))
      .filter(({ pre }) =>
        CURRENT_YEAR_CATEGORIES.some(
          (category) => pre.utilisation[category.availableKey] > 0,
        ),
      );

    for (const category of CURRENT_YEAR_CATEGORIES) {
      for (const { company: transferor, pre } of transferors) {
        const caStillTransferable =
          pre.utilisation.currentYearCAAvailable -
            adjustments[transferor.id].caTransfer >
          0;
        const lossesStillTransferable =
          pre.utilisation.currentYearLossesAvailable -
            adjustments[transferor.id].lossesTransfer >
          0;
        if (category.key === "losses" && caStillTransferable) continue;
        if (
          category.key === "donations" &&
          (caStillTransferable || lossesStillTransferable)
        )
          continue;

        let remainingCategoryAmount = Math.max(
          0,
          pre.utilisation[category.availableKey] -
            adjustments[transferor.id][category.transferKey],
        );
        if (remainingCategoryAmount <= 0) continue;

        const transferorQualifyingDays = getQualifyingDays(
          transferor,
          basisDays,
        );
        const transferorEligibleCap = Math.round(
          (transferorQualifyingDays / basisDays) *
            pre.utilisation[category.availableKey],
        );
        remainingCategoryAmount = Math.min(
          remainingCategoryAmount,
          transferorEligibleCap,
        );

        while (remainingCategoryAmount > 0) {
          const availableThisRound = remainingCategoryAmount;
          const rankedClaimants = orderedCompanies
            .filter((claimant) => claimant.id !== transferor.id)
            .map((claimant) => {
              const claimantQualifyingDays = getQualifyingDays(
                claimant,
                basisDays,
              );
              const pairQualifyingDays = Math.min(
                transferorQualifyingDays,
                claimantQualifyingDays,
              );
              const grossCapacity = calculateClaimCapacity(
                claimant,
                caseSettings,
                adjustments,
              );
              const apportionedCapacity = Math.round(
                (pairQualifyingDays / basisDays) * grossCapacity,
              );
              const amount = Math.min(
                availableThisRound,
                grossCapacity,
                apportionedCapacity,
              );
              return {
                claimant,
                amount,
                pairQualifyingDays,
                taxSaving: scoreClaimant(
                  claimant,
                  caseSettings,
                  adjustments,
                  category.transferKey,
                  Math.min(amount, 1000),
                ),
              };
            })
            .filter((candidate) => candidate.amount > 0)
            .sort((a, b) => {
              if (b.taxSaving !== a.taxSaving) return b.taxSaving - a.taxSaving;
              if (b.amount !== a.amount) return b.amount - a.amount;
              return a.claimant.stableOrder - b.claimant.stableOrder;
            });

          const selected = rankedClaimants[0];
          if (!selected) break;
          const amount = Math.floor(selected.amount);
          if (amount <= 0) break;

          adjustments[transferor.id][category.transferKey] += amount;
          adjustments[selected.claimant.id][category.transferKey] -= amount;
          addToTransfer(
            transfers,
            transferor,
            selected.claimant,
            selected.pairQualifyingDays,
            category.key,
            amount,
          );
          remainingCategoryAmount -= amount;
        }
      }
    }

    const postComputations = orderedCompanies.map((company) =>
      calculateCompanyComputation(
        company,
        caseSettings,
        adjustments[company.id],
      ),
    );
    const afterSummary = summariseComputations(postComputations);
    const transferList = Array.from(transfers.values()).sort((a, b) => {
      const transferorA = orderedCompanies.find(
        (company) => company.id === a.transferorId,
      );
      const transferorB = orderedCompanies.find(
        (company) => company.id === b.transferorId,
      );
      if (transferorA.stableOrder !== transferorB.stableOrder)
        return transferorA.stableOrder - transferorB.stableOrder;
      const claimantA = orderedCompanies.find(
        (company) => company.id === a.claimantId,
      );
      const claimantB = orderedCompanies.find(
        (company) => company.id === b.claimantId,
      );
      return claimantA.stableOrder - claimantB.stableOrder;
    });

    return {
      schemaVersion: 1,
      settings: { ...caseSettings },
      companies: orderedCompanies.map((company) => ({
        ...company,
        continuousQualifyingDays: getQualifyingDays(company, basisDays),
      })),
      preComputations,
      postComputations,
      adjustments,
      transfers: transferList,
      beforeSummary,
      afterSummary,
      taxSaving: beforeSummary.taxPayable - afterSummary.taxPayable,
      totalTransferred: transferList.reduce(
        (sum, transfer) => sum + transfer.totalTransferred,
        0,
      ),
      relationshipCount: transferList.length,
      reconciliation: reconcileTransfers(adjustments),
      explanation: buildExplanation(caseSettings, transferList),
    };
  }

  function reconcileTransfers(adjustments) {
    const totals = Object.values(adjustments).reduce(
      (sum, item) => ({
        caTransfer: sum.caTransfer + item.caTransfer,
        lossesTransfer: sum.lossesTransfer + item.lossesTransfer,
        donationsTransfer: sum.donationsTransfer + item.donationsTransfer,
      }),
      { caTransfer: 0, lossesTransfer: 0, donationsTransfer: 0 },
    );
    return {
      ...totals,
      balanced:
        totals.caTransfer === 0 &&
        totals.lossesTransfer === 0 &&
        totals.donationsTransfer === 0,
    };
  }

  function buildExplanation(caseSettings, transfers) {
    return [
      "The optimiser ranked claims by estimated reduction in group tax payable.",
      "It transferred current-year CA first, then trade losses, then donations.",
      "Each transfer was capped by the transferor's available current-year item, the claimant's remaining income after its own losses and prior-year and current-year donations, and any pair-specific shareholding-period limit.",
      transfers.length
        ? `The recommended solution uses ${transfers.length} transfer relationship${transfers.length === 1 ? "" : "s"}.`
        : "No tax-effective transfer was available from the entered data.",
    ];
  }

  return { optimiseGroupRelief, reconcileTransfers };
}
